<template>
  <div class="ss__feature__popover__wrapper">
    <transition :name="transitionName">
      <div
        ref="SsFeaturePopover"
        v-show="visible"
        class="ss__feature__popover">
        <button v-if="showClose" class="btn__close" @click="close">
          <i class="ssCommunityIcon__cancel"></i>
        </button>
        <div class="popover__title">
          {{ title }}
          <Star v-if="showStar" class="popover__title__star"/>
        </div>
        <div class="popover__content" v-html="content"></div>
        <template>
          <slot/>
        </template>
        <span class="popover__triangle" ref="triangle">
          <Triangle />
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import throttle from 'lodash/throttle'
import Star from './star.vue'
import Triangle from './triangle.vue'

export default {
  name: 'SsFeaturePopover',

  components: {
    Star,
    Triangle
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: false
    },
    target: {
      type: String
    },
    targetEl: {},
    spacing: {
      type: Number,
      default: 16
    },
    transitionName: {
      type: String,
      default: 'body'
    },
    showStar: {
      type: Boolean,
      default: true
    },
    showClose: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      rect: {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      }
    }
  },

  watch: {
    visible () {
      this.$nextTick(this.calcRect)
    }
  },

  methods: {
    close () {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    calcRect: throttle(function () {
      if (this.visible && (this.target || this.targetEl)) {
        if (this.target) {
          this.rect = document.querySelector(this.target).getBoundingClientRect()
        }
        if (this.targetEl) {
          this.rect = this.targetEl.getBoundingClientRect()
        }
        const popoverRect = this.$refs.SsFeaturePopover.getBoundingClientRect()
        const triangleHeight = 6
        const winWidth = window.innerWidth
        const xLimit = (popoverRect.width - this.rect.width) / 2
        const yLimit = popoverRect.height + triangleHeight + this.spacing
        let left = 0
        let top = 0
        let originX = 0
        let originY = this.$round((yLimit + this.rect.height / 2) / 99 * 100, 2, 'number')
        if (this.rect.x <= xLimit) {
          left = 8
          originX = this.$round((this.rect.x + this.rect.width / 2) / popoverRect.width * 100, 2, 'number')
        } else if (winWidth - this.rect.x - this.rect.width <= xLimit) {
          left = winWidth - popoverRect.width - 8
          originX = this.$round((this.rect.x - left + this.rect.width / 2) / popoverRect.width * 100, 2, 'number')
        } else {
          left = this.rect.x - xLimit
          originX = 50
        }
        if (this.rect.y > yLimit) {
          top = this.rect.y - yLimit
          this.$refs.triangle.style.bottom = '1px'
          this.$refs.triangle.style.top = ''
          this.$refs.triangle.style.transform = 'translate(-50%, 100%)'
        } else {
          top = this.rect.y + this.rect.height + this.spacing
          this.$refs.triangle.style.bottom = ''
          this.$refs.triangle.style.top = `-${triangleHeight * 2}px`
          this.$refs.triangle.style.transform = 'translate(-50%, 100%) scaleY(-1)'
        }
        // console.log(this.$refs.triangle)
        this.$refs.SsFeaturePopover.style.left = `${left}px`
        this.$refs.SsFeaturePopover.style.top = `${top}px`
        this.$refs.triangle.style.left = `${originX / 100 * popoverRect.width}px`
        this.$refs.SsFeaturePopover.style.transformOrigin = `${originX}% ${originY}%`
      }
    })
  },

  mounted () {
    window.addEventListener('scroll', this.calcRect)
  },

  destroyed () {
    window.removeEventListener('scroll', this.calcRect)
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/assets/styles/media.styl'
.ss__feature__popover__wrapper {
  .ss__feature__popover {
    position: fixed;
    z-index: 200;
    background: var(--color-SsFeaturePopover-bg, var(--B0-t-overlay));
    box-shadow: var(--shadow-SsFeaturePopover, var(--shadow-level-2));
    border-radius: var(--radius-SsFeaturePopover, 16px);
    padding: var(--padding-SsFeaturePopover, 16px);
    width: var(--width-SsFeaturePopover, 268px);
    box-sizing: border-box;
    .popover__title {
      font-size: var(--text-SsFeaturePopoverTitle, 15px)
      line-height: var(--leading-SsFeaturePopoverTitle, 21px)
      color: var(--color-SsFeaturePopoverTitle, var(--B8-t))
      font-weight: 500
      margin-bottom: 4px
      position relative
      display: inline-block
      .popover__title__star {
        position: absolute
        left: 50%
        top: 50%
        transform: translate(-50%, -50%)
      }
     }
    .popover__content {
      font-size: var(--text-SsFeaturePopoverContent, 15px)
      line-height: var(--leading-SsFeaturePopoverContent, 21px)
      color: var(--color-SsFeaturePopoverContent, var(--B6-t))
    }
    .btn__close {
      position: absolute
      top: 0px
      right: 0px
      padding: 16px
      font-size: 16px
      opacity .3
    }
    .popover__triangle {
      position: absolute
      line-height: 0
    }
  }

  .mask-enter-active,
  .mask-leave-active {
    opacity: 1
    transition: all var(--a-dur-default) var(--a-func-default)
  }

  .mask-enter,
  .mask-leave-to {
    opacity: 0
  }

  .body-enter-active,
  .body-leave-active {
    opacity: 1;
    transform: rotate(0deg) translateY(0);
    transition: all 0.3s;
  }

  .body-enter-active {
    opacity 1
    animation: .3s ease-in-out bounce-in;
  }

  .body-leave-active {
    opacity 1
    animation: .3s ease-in-out reverse bounce-in;
  }

  .body-enter,
  .body-leave-to {
    opacity: 0
  }

  @keyframes bounce-in {
    0% {
      transform: rotate(-3deg) translateY(20px)
    }
    50% {
      transform: rotate(2deg) translateY(0px)
    }
    70% {
      transform: rotate(1deg) translateY(0px)
    }
    100% {
      transform: rotate(0deg) translateY(0px)
    }
  }
}
</style>
